import { useState } from 'react';
import { motion } from 'motion/react';
import { HardHat, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { WorkerRole, type Worker } from '@/data/types';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Form = { name: string; role: WorkerRole; active: boolean };
const blank = (): Form => ({
  name: '',
  role: WorkerRole.TECHNICIAN,
  active: true,
});

export function Workers() {
  const t = useT();
  const workers = useApp(s => s.workers);
  const upsertWorker = useApp(s => s.upsertWorker);
  const deleteWorker = useApp(s => s.deleteWorker);
  const toast = useApp(s => s.toast);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Worker | null>(null);
  const [form, setForm] = useState<Form>(blank());

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (w: Worker) => {
    setEditing(w);
    setForm({ name: w.name, role: w.role, active: w.active });
    setOpen(true);
  };
  const handleSave = () => {
    if (!form.name.trim()) return;
    upsertWorker({
      id: editing?.id ?? `W-${Date.now()}`,
      name: form.name.trim(),
      role: form.role,
      active: form.active,
    });
    toast(editing ? t('common.save') : t('workers.newWorker'));
    setOpen(false);
  };
  const handleDelete = (w: Worker) => {
    if (!confirm(`${t('common.delete')} ${w.name}?`)) return;
    deleteWorker(w.id);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HardHat className="h-6 w-6 text-primary" /> {t('workers.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('workers.subtitle')}
          </p>
        </div>
        <Button size="sm" onClick={openCreate} data-focus="workers-new">
          <Plus className="h-4 w-4" />
          {t('workers.newWorker')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            {t('workers.team')} ({workers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.avatar')}</TableHead>
                <TableHead>{t('table.name')}</TableHead>
                <TableHead>{t('table.role')}</TableHead>
                <TableHead>{t('table.status')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {workers.map(w => {
                const initials = w.name
                  .split(' ')
                  .map(n => n[0])
                  .slice(0, 2)
                  .join('');
                return (
                  <TableRow key={w.id}>
                    <TableCell>
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                        {initials}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{w.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {w.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={w.active ? 'success' : 'outline'}>
                        {w.active ? t('workers.active') : t('workers.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          onClick={() => openEdit(w)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          onClick={() => handleDelete(w)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('workers.newWorker')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('table.name')}</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('table.role')}</Label>
              <Select
                value={form.role}
                onValueChange={v =>
                  setForm(f => ({ ...f, role: v as WorkerRole }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={WorkerRole.SUPERADMIN}
                    className="capitalize"
                  >
                    {WorkerRole.SUPERADMIN}
                  </SelectItem>
                  <SelectItem value={WorkerRole.ADMIN} className="capitalize">
                    {WorkerRole.ADMIN}
                  </SelectItem>
                  <SelectItem value={WorkerRole.STAFF} className="capitalize">
                    {WorkerRole.STAFF}
                  </SelectItem>
                  <SelectItem
                    value={WorkerRole.TECHNICIAN}
                    className="capitalize"
                  >
                    {WorkerRole.TECHNICIAN}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={e =>
                  setForm(f => ({ ...f, active: e.target.checked }))
                }
                className="h-4 w-4 accent-primary"
              />
              <span className="text-sm">{t('workers.active')}</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
